import React from "react";
import { useTranslation } from "react-i18next";
import NotificationsPortal, { notify } from "./NotificationsPortal";
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";

export default function EmpresaPanel() {
  const { t } = useTranslation();
  const { user, orgId, userRole } = useAuth();

  const DEFAULT_LOGO = React.useMemo(
    () => `${process.env.PUBLIC_URL}/img/avatar2.jpg`,
    [],
  );

  const [logoUrl, setLogoUrl] = React.useState(DEFAULT_LOGO);
  const [logoName, setLogoName] = React.useState("");
  const [razonSocial, setRazonSocial] = React.useState("—");
  const [rfc, setRfc] = React.useState("—");
  const [industria, setIndustria] = React.useState("—");
  const [empleados, setEmpleados] = React.useState("—");
  const [telefono, setTelefono] = React.useState("—");
  const [sitioWeb, setSitioWeb] = React.useState("—");

  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    logoFile: null,
    razonSocial: "",
    rfc: "",
    industria: "",
    empleados: "",
    telefono: "",
    sitioWeb: "",
  });

  const fileInputRef = React.useRef(null);
  const openPicker = () => fileInputRef.current?.click();
  const hasLogo = logoUrl && !logoUrl.includes("avatar2.jpg");

  // ✅ Permiso: solo admin/owner edita empresa (ajústalo a tus roles reales)
  const canEdit =
    userRole === "Administrador" ||
    userRole === "Administrator" ||
    userRole === "Owner";

  React.useEffect(() => {
    if (!user?.uid || !orgId) return;

    const orgRef = doc(db, "orgs", orgId);
    const unsub = onSnapshot(
      orgRef,
      (snap) => {
        if (!snap.exists()) {
          // org doc no existe aún
          setLogoUrl(DEFAULT_LOGO);
          setLogoName("");
          setRazonSocial("—");
          setRfc("—");
          setIndustria("—");
          setEmpleados("—");
          setTelefono("—");
          setSitioWeb("—");
          return;
        }

        const data = snap.data() || {};

        setLogoUrl(data.logoUrl ? data.logoUrl : DEFAULT_LOGO);
        setLogoName(data.logoName ? data.logoName : "");

        setRazonSocial(data.name ?? data.razonSocial ?? "—");
        setRfc(data.rfc ?? "—");
        setIndustria(data.industry ?? data.industria ?? "—");
        setEmpleados(data.employeesRange ?? data.empleados ?? "—");
        setTelefono(data.phone ?? data.telefono ?? "—");
        setSitioWeb(data.website ?? data.sitioWeb ?? "—");
      },
      (err) => {
        if (err?.code === "permission-denied") return;
        console.error("[EmpresaPanel orgs/{orgId}]", err);
      },
    );

    return () => unsub();
  }, [user?.uid, orgId, DEFAULT_LOGO]);

  const startEdit = () => {
    if (!canEdit) {
      notify(
        "error",
        t("errors.noPermission", "No tienes permisos para editar."),
      );
      return;
    }

    setForm({
      logoFile: null,
      razonSocial: razonSocial === "—" ? "" : razonSocial,
      rfc: rfc === "—" ? "" : rfc,
      industria: industria === "—" ? "" : industria,
      empleados: empleados === "—" ? "" : empleados,
      telefono: telefono === "—" ? "" : telefono,
      sitioWeb: sitioWeb === "—" ? "" : sitioWeb,
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm((f) => ({ ...f, logoFile: null }));
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "logoFile") {
      const file = files?.[0] || null;
      e.target.value = "";
      if (file) await handleLogoSelected(file);
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleDeleteLogo = async () => {
    try {
      if (!user?.uid) throw new Error(t("errors.noSession", "No hay sesión."));
      if (!orgId) throw new Error("No orgId.");

      if (!canEdit) {
        notify(
          "error",
          t("errors.noPermission", "No tienes permisos para editar."),
        );
        return;
      }

      const orgRef = doc(db, "orgs", orgId);

      // ✅ 1) borrar del Storage si existe y no es default
      if (logoName && hasLogo) {
        try {
          const fileRef = storageRef(storage, logoName); // logoName ahora es path
          await deleteObject(fileRef);
        } catch (e) {
          // si ya no existe, no detengas el flujo
          if (e?.code !== "storage/object-not-found") throw e;
        }
      }

      // ✅ 2) limpiar Firestore
      await updateDoc(orgRef, {
        logoUrl: "",
        logoName: "",
        updatedAt: serverTimestamp(),
      });

      // ✅ 3) estado local
      setLogoUrl(DEFAULT_LOGO);
      setLogoName("");

      notify("success", t("toasts.logoDeleted", "Logotipo eliminado."));
    } catch (err) {
      notify("error", `Error al eliminar: ${err?.message || "—"}`);
    }
  };

  const handleLogoSelected = async (file) => {
    let tempUrl = null;

    try {
      if (!user?.uid) throw new Error(t("errors.noSession", "No hay sesión."));
      if (!orgId) throw new Error("No orgId.");
      if (!file) return;

      if (!canEdit) {
        notify(
          "error",
          t("errors.noPermission", "No tienes permisos para editar."),
        );
        return;
      }

      // preview inmediato
      tempUrl = URL.createObjectURL(file);
      setLogoUrl(tempUrl);
      // ❌ ya no guardes file.name aquí porque ahora logoName será path
      // setLogoName(file.name);

      if (file.size > 2 * 1024 * 1024) {
        notify("error", t("toasts.fileTooLarge", "El logotipo excede 2MB."));
        setLogoUrl(DEFAULT_LOGO);
        setLogoName("");
        return;
      }

      // ✅ 1) Borrar logo anterior (si existe y no es default)
      if (logoName && hasLogo) {
        try {
          await deleteObject(storageRef(storage, logoName)); // logoName ahora es path
        } catch (e) {
          // si ya no existe, no detengas el flujo
          if (e?.code !== "storage/object-not-found") throw e;
        }
      }

      // ✅ 2) Subir nuevo logo
      const path = `orgLogos/${orgId}/${Date.now()}-${file.name}`;
      const fileRef = storageRef(storage, path);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      // ✅ 3) Guardar en Firestore: url + path
      const orgRef = doc(db, "orgs", orgId);
      await updateDoc(orgRef, {
        logoUrl: url,
        logoName: path, // ✅ guarda la ruta real
        updatedAt: serverTimestamp(),
      });

      // ✅ 4) Estado local
      setLogoUrl(url);
      setLogoName(path);

      notify("success", t("toasts.logoUpdated", "Logotipo actualizado."));
    } catch (err) {
      setLogoUrl(DEFAULT_LOGO);
      setLogoName("");
      notify(
        "error",
        t("toasts.logoUploadError", {
          defaultValue: "Error al subir logo: {{msg}}",
          msg: err?.message || "—",
        }),
      );
    } finally {
      if (tempUrl) URL.revokeObjectURL(tempUrl);
    }
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      if (!user?.uid) throw new Error(t("errors.noSession", "No hay sesión."));
      if (!orgId) throw new Error("No orgId.");

      if (!canEdit) {
        notify(
          "error",
          t("errors.noPermission", "No tienes permisos para editar."),
        );
        return;
      }

      const orgRef = doc(db, "orgs", orgId);

      // 1) Subir logo opcional (si usas esta ruta, quizá ya no la necesitas porque handleLogoSelected ya lo hace)
      let newLogoUrl = null;
      let newLogoName = logoName;

      if (form.logoFile) {
        if (form.logoFile.size > 2 * 1024 * 1024) {
          notify("error", t("toasts.fileTooLarge", "El logotipo excede 2MB."));
          return;
        }

        const path = `orgLogos/${orgId}/${Date.now()}-${form.logoFile.name}`;
        const ref = storageRef(storage, path);

        await uploadBytes(ref, form.logoFile);
        newLogoUrl = await getDownloadURL(ref);
        newLogoName = form.logoFile.name;
      }

      // 2) payload org
      const payload = {
        name: (form.razonSocial || "").trim() || "—",
        rfc: (form.rfc || "").trim().toUpperCase() || "—",
        industry: (form.industria || "").trim() || "—",
        employeesRange: (form.empleados || "").trim() || "—",
        phone: (form.telefono || "").trim() || "—",
        website: (form.sitioWeb || "").trim() || "—",
        updatedAt: serverTimestamp(),
      };

      // si hubo nuevo logo
      if (newLogoUrl !== null) payload.logoUrl = newLogoUrl;
      if (newLogoName !== null) payload.logoName = newLogoName;

      await updateDoc(orgRef, payload);

      if (newLogoUrl) setLogoUrl(newLogoUrl);
      setLogoName(newLogoName);

      setRazonSocial(payload.name);
      setRfc(payload.rfc);
      setIndustria(payload.industry);
      setEmpleados(payload.employeesRange);
      setTelefono(payload.phone);
      setSitioWeb(payload.website);

      notify(
        "success",
        t("toasts.companyUpdated", "Datos de la empresa actualizados."),
      );
      setIsEditing(false);
      setForm((f) => ({ ...f, logoFile: null }));
    } catch (err) {
      notify(
        "error",
        t("toasts.saveError", {
          defaultValue: "Error al guardar: {{msg}}",
          msg: err?.message || "—",
        }),
      );
    }
  };

  return (
    <>
      <NotificationsPortal />

      <div className="config-section">
        <div className={`empresa-card ${!isEditing ? "is-readonly" : ""}`}>
          <div className="empresa-head">
            <div className="empresa-title">
              {t("company.title", "Información de la Empresa")}
            </div>

            {!isEditing ? (
              <button
                type="button"
                className="info-edit"
                onClick={startEdit}
                disabled={!canEdit}
                title={
                  !canEdit
                    ? t(
                        "errors.noPermission",
                        "No tienes permisos para editar.",
                      )
                    : ""
                }
              >
                <i className="bi bi-pencil" />
                <span>{t("actions.edit", "Editar")}</span>
              </button>
            ) : (
              <div className="empresa-actions">
                <button
                  type="button"
                  className="empresa-cancel"
                  onClick={cancelEdit}
                >
                  <i className="bi bi-x-lg" />
                  <span>{t("actions.cancel", "Cancelar")}</span>
                </button>

                <button
                  type="button"
                  className="info-save"
                  onClick={handleSave}
                >
                  <i className="bi bi-save" />
                  <span>{t("actions.save", "Guardar")}</span>
                </button>
              </div>
            )}
          </div>

          <div className="empresa-divider" />

          <div className="empresa-grid">
            <div className="empresa-field empresa-span-2">
              <label>
                {t("company.businessName", "Razón Social")}{" "}
                <span className="empresa-req">*</span>
              </label>

              {!isEditing ? (
                <div className="empresa-input">{razonSocial}</div>
              ) : (
                <input
                  className="empresa-control"
                  name="razonSocial"
                  value={form.razonSocial}
                  onChange={handleChange}
                />
              )}
            </div>

            <div className="empresa-field">
              <label>
                {t("company.taxId", "RFC")}{" "}
                <span className="empresa-req">*</span>
              </label>

              {!isEditing ? (
                <div className="empresa-input">{rfc}</div>
              ) : (
                <input
                  className="empresa-control"
                  name="rfc"
                  value={form.rfc}
                  onChange={handleChange}
                />
              )}
            </div>

            <div className="empresa-field">
              <label>{t("company.industry", "Industria")}</label>

              {!isEditing ? (
                <div className="empresa-input empresa-input-select">
                  <span>{industria}</span>
                  <i className="bi bi-chevron-down" />
                </div>
              ) : (
                <select
                  className="empresa-control"
                  name="industria"
                  value={form.industria}
                  onChange={handleChange}
                >
                  <option value="">{t("common.select")}</option>
                  <option value="Manufactura">
                    {t("company.industries.manufacturing", "Manufactura")}
                  </option>
                  <option value="Comercio">
                    {t("company.industries.commerce", "Comercio")}
                  </option>
                  <option value="Servicios">
                    {t("company.industries.services", "Servicios")}
                  </option>
                  <option value="Tecnología">
                    {t("company.industries.tech", "Tecnología")}
                  </option>
                  <option value="Construcción">
                    {t("company.industries.construction", "Construcción")}
                  </option>
                  <option value="Otro">
                    {t("company.industries.other", "Otro")}
                  </option>
                </select>
              )}
            </div>

            <div className="empresa-field">
              <label>{t("company.employees", "Número de empleados")}</label>

              {!isEditing ? (
                <div className="empresa-input empresa-input-select">
                  <span>{empleados}</span>
                  <i className="bi bi-chevron-down" />
                </div>
              ) : (
                <select
                  className="empresa-control"
                  name="empleados"
                  value={form.empleados}
                  onChange={handleChange}
                >
                  <option value="">{t("common.select")}</option>
                  <option value="1-10">1-10</option>
                  <option value="11-50">11-50</option>
                  <option value="50-100">50-100</option>
                  <option value="100-250">101-250</option>
                  <option value="250+">250+</option>
                </select>
              )}
            </div>

            <div className="empresa-field">
              <label>{t("personal.phone", "Teléfono")}</label>

              {!isEditing ? (
                <div className="empresa-input">
                  <i className="bi bi-telephone" />
                  <span>{telefono}</span>
                </div>
              ) : (
                <input
                  className="empresa-control"
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  placeholder={t(
                    "company.phonePlaceholder",
                    "+52 55 1234 5678",
                  )}
                />
              )}
            </div>

            <div className="empresa-field empresa-span-2">
              <label>{t("company.website", "Sitio web")}</label>

              {!isEditing ? (
                <div className="empresa-input">
                  <i className="bi bi-globe2" />
                  <span>{sitioWeb}</span>
                </div>
              ) : (
                <div className="empresa-control-with-icon">
                  <i className="bi bi-globe2" />
                  <input
                    className="empresa-control empresa-control-inner"
                    name="sitioWeb"
                    value={form.sitioWeb}
                    onChange={handleChange}
                    placeholder={t(
                      "company.websitePlaceholder",
                      "www.miempresa.com",
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CARD 2: LOGO */}
        <div className="empresa-card">
          <div className="empresa-head">
            <div className="empresa-title">
              {t("company.logoTitle", "Logo de la Empresa")}
            </div>
          </div>

          <div className="empresa-divider" />

          <div className="empresa-logo-row">
            <div className="empresa-logo-icon">
              {hasLogo ? (
                <img
                  className="empresa-logo-thumb"
                  src={logoUrl}
                  alt={t("company.logoTitle", "Logo de la Empresa")}
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_LOGO;
                  }}
                />
              ) : (
                <i className="bi bi-image"></i>
              )}
            </div>

            <div className="empresa-logo-meta">
              <div className="empresa-logo-text">
                {t(
                  "company.logoHelp",
                  "Logo de la empresa (formato JPG, PNG o SVG, tamaño máximo 2MB)",
                )}
              </div>

              <div className="empresa-logo-actions">
                <button
                  type="button"
                  className="empresa-upload"
                  onClick={openPicker}
                  disabled={!canEdit}
                  title={
                    !canEdit
                      ? t(
                          "errors.noPermission",
                          "No tienes permisos para editar.",
                        )
                      : ""
                  }
                >
                  <i className="bi bi-upload" />
                  {hasLogo
                    ? t("company.changeLogo", "Cambiar logo")
                    : t("company.uploadLogo", "Subir logo")}
                </button>

                {hasLogo && (
                  <button
                    type="button"
                    className="empresa-delete"
                    onClick={handleDeleteLogo}
                    disabled={!canEdit}
                    title={
                      !canEdit
                        ? t(
                            "errors.noPermission",
                            "No tienes permisos para editar.",
                          )
                        : ""
                    }
                  >
                    <i className="bi bi-x-lg" />
                    {t("actions.delete", "Eliminar")}
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                name="logoFile"
                onChange={handleChange}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
